import React, { ReactNode, useEffect } from "react";
import { FixedObject } from "gatsby-image";
import {
  useUpdateGlobalConfiguration,
  TrWebProvider,
  SegmentAnalyticsService,
  countryIsoCodeFromLocale,
  getUtmParams,
  setEventMetadataUtm,
} from "tr-web";
import { Helmet } from "react-helmet";
import { Locale } from "../../config/sites-and-locales";
import { TranslationProvider } from "./PageBase/TranslationProvider";
import { StyleProvider } from "./PageBase/StyleProvider";
import { SEO } from "./PageBase/SEO";
import { BugsnagErrorBoundary } from "./PageBase/BugsnagErrorBoundary";

export interface PageBaseProps {
  canonicalUrl: string;
  children: ReactNode;
  hreflangData?: any; // TODO: Make this type declaration work (very frustrating)
  locale: Locale;
  metaTitle?: any;
  noIndexMeta: boolean;
  pageDescription?: any;
  pageImage?: FixedObject;
  /** Page name for the Segment page tracking */
  pageName?: string;
  pageTitle?: any;
  serviceType?: string;
}

export function PageBase({
  canonicalUrl,
  children,
  hreflangData,
  locale,
  metaTitle,
  noIndexMeta,
  pageDescription,
  pageImage,
  pageName,
  pageTitle,
  serviceType,
}: PageBaseProps) {
  useUpdateGlobalConfiguration({ locale });

  useEffect(() => {
    if (window.location.search) {
      const utmParams = getUtmParams(window.location.search);
      setEventMetadataUtm(utmParams);
    }

    SegmentAnalyticsService.trackPage({
      name: pageName,
      properties: { country_iso_code: countryIsoCodeFromLocale(locale) },
    });
  }, [locale, pageName]);

  return (
    <>
      <DomainPreconnector />
      <DigiohScript locale={locale} />
      <BugsnagErrorBoundary>
        <TranslationProvider locale={locale}>
          <SEO
            canonicalUrl={canonicalUrl}
            hreflangData={hreflangData}
            locale={locale}
            metaTitle={metaTitle}
            noIndexMeta={noIndexMeta}
            pageDescription={pageDescription}
            pageImage={pageImage}
            pageTitle={pageTitle}
            serviceType={serviceType}
          />
          <StyleProvider>
            <TrWebProvider locale={locale}>{children}</TrWebProvider>
          </StyleProvider>
        </TranslationProvider>
      </BugsnagErrorBoundary>
    </>
  );
}

function DomainPreconnector() {
  return (
    <Helmet>
      <link rel="preconnect" href="https://res.cloudinary.com" />
    </Helmet>
  );
}

function DigiohScript({ locale }: { locale: Locale }) {
  const digiohUrl =
    "https://www.lightboxcdn.com/vendor/4921a0e7-128c-4d67-8262-48575b9dd75f/lightbox_speed.js";

  const isNorthAmerica = ["en-US", "en-CA", "fr-CA"].includes(locale);
  const className = !isNorthAmerica ? "optanon-category-C0003" : "";
  const type = isNorthAmerica ? "text/javascript" : "text/plain";
  return (
    <Helmet>
      <script className={className} src={digiohUrl} type={type} />
    </Helmet>
  );
}
